import React, { useState, useEffect } from 'react';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { withLocalePrefix } from 'shared/utils/translation';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { Body, SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import Checkbox from '@grnhse/seedling/lib/birch/components/checkbox';
import SkeletonLoader from '@grnhse/seedling/lib/birch/components/skeleton_loader';
import ArrowUpIcon from '@grnhse/seedling/lib/birch/icons/increase_arrow';
import ArrowDownIcon from '@grnhse/seedling/lib/birch/icons/decrease_arrow';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import styled from 'styled-components';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { useCalibrationSuggestions } from 'talent_matching/api';
import { useCalibrationModal } from 'talent_matching/contexts/calibration_modal_context';
import { CalibrationSuggestion } from 'talent_matching/types';
import NudgeSuggestionsNarrow from 'talent_matching/components/modals/calibration_nudge_suggestions_narrow';

const t = withLocalePrefix('talent_matching.modals.calibration_nudge');
const tImportance = withLocalePrefix(
  'talent_matching.modals.calibration.importance'
);

const IMPORTANCE_KEY_MAP: Record<number, string> = {
  1: 'not_very_important',
  2: 'somewhat_important',
  3: 'important',
  4: 'very_important',
  5: 'crucial',
};

const importanceLabel = (value: number) =>
  tImportance(IMPORTANCE_KEY_MAP[value] ?? 'important');

const SuggestionsContainer = styled.div`
  background-color: ${FullPalette.GREY_E25};
  border-radius: 4px;
  padding: ${Tokens.spacing[2]};
  display: flex;
  flex-direction: column;
  gap: ${Tokens.spacing[1]};
`;

const HorizontalDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${FullPalette.GREY_700};
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Tokens.spacing[1]};
`;

const SkillCard = styled.div<{ $checked: boolean }>`
  background: white;
  border: ${(props) =>
    props.$checked
      ? `1px solid ${FullPalette.GREEN_700}`
      : `1px solid ${FullPalette.GREY_700}`};
  border-radius: 4px;
  padding: ${Tokens.spacing['1.5']} 0;
`;

const ROW_COLUMNS = `grid-template-columns: 1fr 150px 150px`;

const SelectAllRow = styled.div`
  display: grid;
  ${ROW_COLUMNS};
  align-items: center;
  padding: 0 ${Tokens.spacing['1.5']};
`;

const NameCol = styled.div`
  min-width: 0;
`;

const ColumnHeader = styled.span`
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  color: #526b63;
`;

const SkillRow = styled.div`
  display: grid;
  ${ROW_COLUMNS};
  align-items: center;
  padding: 0 ${Tokens.spacing['1.5']};
`;

const SuggestedCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${Tokens.spacing['0.5']};
`;

const NARROW_BREAKPOINT = '1200px';

const WideOnly = styled.div`
  @media (max-width: ${NARROW_BREAKPOINT}) {
    display: none;
  }
`;

const NarrowOnly = styled.div`
  display: none;
  @media (max-width: ${NARROW_BREAKPOINT}) {
    display: block;
  }
`;

export type CalibrationNudgeVariant = 'initial' | 'review';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  canCreateCalibrationSetting: boolean;
  openCalibrationModal: () => void;
  onApplySuggestions?: (selected: CalibrationSuggestion[]) => void;
  onManualReview?: () => void;
  variant?: CalibrationNudgeVariant;
};

const CalibrationNudgeModal = ({
  isOpen,
  onClose,
  canCreateCalibrationSetting,
  openCalibrationModal,
  onApplySuggestions,
  onManualReview,
  variant = 'initial',
}: Props) => {
  const { paths } = useCalibrationModal();

  const { data, isFetching } = useCalibrationSuggestions(
    paths,
    isOpen && canCreateCalibrationSetting
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const suggestions = data?.suggestions ?? [];
  const hasSuggestions = suggestions.length > 0;

  const [checkedNames, setCheckedNames] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (hasSuggestions) {
      setCheckedNames(new Set(suggestions.map((s) => s.name)));
    }
  }, [hasSuggestions, suggestions]);

  const allChecked =
    suggestions.length > 0 && checkedNames.size === suggestions.length;
  const someChecked = checkedNames.size > 0 && !allChecked;

  const toggleAll = () => {
    if (allChecked) {
      setCheckedNames(new Set());
    } else {
      setCheckedNames(new Set(suggestions.map((s) => s.name)));
    }
  };

  const toggleOne = (name: string) => {
    setCheckedNames((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const handleApply = () => {
    const selected = suggestions.filter((s) => checkedNames.has(s.name));
    onApplySuggestions?.(selected);
    onClose();
    openCalibrationModal();
  };

  const handleManualReview = () => {
    onManualReview?.();
    onClose();
    openCalibrationModal();
  };

  const handleSimpleReview = () => {
    onClose();
    openCalibrationModal();
  };

  if (!canCreateCalibrationSetting) {
    return (
      <DataEntryModal
        isOpen={isOpen}
        onDismiss={onClose}
        ariaLabel={t('header')}
        size="large"
      >
        <DataEntryModal.Header onClick={onClose}>
          {t('header')}
        </DataEntryModal.Header>
        <DataEntryModal.Body>
          <Body>{t('description')}</Body>
          <Body>{t('team_review')}</Body>
        </DataEntryModal.Body>
        <DataEntryModal.Footer>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <PrimaryButton onClick={onClose}>
              {t('actions.got_it')}
            </PrimaryButton>
          </Stack>
        </DataEntryModal.Footer>
      </DataEntryModal>
    );
  }

  if (isFetching) {
    return (
      <DataEntryModal
        isOpen={isOpen}
        onDismiss={onClose}
        ariaLabel={t('header')}
        size="large"
      >
        <DataEntryModal.Header onClick={onClose}>
          {t('header')}
        </DataEntryModal.Header>
        <DataEntryModal.Body>
          <SkeletonLoader columnsPerRow={1} rows={3} />
        </DataEntryModal.Body>
      </DataEntryModal>
    );
  }

  if (!hasSuggestions) {
    return (
      <DataEntryModal
        isOpen={isOpen}
        onDismiss={onClose}
        ariaLabel={t('header')}
        size="large"
      >
        <DataEntryModal.Header onClick={onClose}>
          {t('header')}
        </DataEntryModal.Header>
        <DataEntryModal.Body>
          <Body>{t('description')}</Body>
          <Body>{t('review_calibration')}</Body>
        </DataEntryModal.Body>
        <DataEntryModal.Footer>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <SecondaryButton onClick={onClose}>
              {t('actions.go_back')}
            </SecondaryButton>
            <PrimaryButton onClick={handleSimpleReview}>
              {t('actions.review_calibration')}
            </PrimaryButton>
          </Stack>
        </DataEntryModal.Footer>
      </DataEntryModal>
    );
  }

  const header = variant === 'review' ? t('review_header') : t('header');
  const description =
    variant === 'review'
      ? t('review_description')
      : t('description_with_suggestions');

  return (
    <DataEntryModal
      isOpen={isOpen}
      onDismiss={onClose}
      ariaLabel={header}
      size="large"
    >
      <DataEntryModal.Header onClick={onClose}>{header}</DataEntryModal.Header>

      <DataEntryModal.Body>
        <Stack spacing={2}>
          <Body>{description}</Body>
          <SecondaryBody>{t('select_updates')}</SecondaryBody>
          <WideOnly>
            <SuggestionsContainer>
              <SelectAllRow>
                <NameCol>
                  <Checkbox
                    label={t('actions.select_all')}
                    value="select-all"
                    checked={allChecked}
                    indeterminate={someChecked}
                    onChange={toggleAll}
                  />
                </NameCol>
                <ColumnHeader>{t('columns.current')}</ColumnHeader>
                <ColumnHeader>{t('columns.suggested')}</ColumnHeader>
              </SelectAllRow>
              <HorizontalDivider />
              <CardList>
                {suggestions.map((suggestion) => {
                  const isChecked = checkedNames.has(suggestion.name);
                  const direction =
                    suggestion.suggested_importance >
                    suggestion.current_importance
                      ? 'up'
                      : 'down';
                  const iconColor =
                    direction === 'up'
                      ? FullPalette.GREEN_700
                      : FullPalette.POPPY_600;
                  const ArrowIcon =
                    direction === 'up' ? ArrowUpIcon : ArrowDownIcon;
                  return (
                    <SkillCard key={suggestion.name} $checked={isChecked}>
                      <SkillRow>
                        <NameCol>
                          <Checkbox
                            label={suggestion.name}
                            value={suggestion.name}
                            checked={isChecked}
                            onChange={() => toggleOne(suggestion.name)}
                          />
                        </NameCol>
                        <Body metadata>
                          {importanceLabel(suggestion.current_importance)}
                        </Body>
                        <SuggestedCell>
                          <ArrowIcon size="sm" color={iconColor} />
                          <Body medium>
                            {importanceLabel(suggestion.suggested_importance)}
                          </Body>
                        </SuggestedCell>
                      </SkillRow>
                    </SkillCard>
                  );
                })}
              </CardList>
            </SuggestionsContainer>
          </WideOnly>
          <NarrowOnly>
            <NudgeSuggestionsNarrow
              suggestions={suggestions}
              checkedNames={checkedNames}
              allChecked={allChecked}
              someChecked={someChecked}
              toggleAll={toggleAll}
              toggleOne={toggleOne}
            />
          </NarrowOnly>
        </Stack>
      </DataEntryModal.Body>

      <DataEntryModal.Footer>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <TertiaryButton onClick={onClose}>
            {t('actions.close')}
          </TertiaryButton>
          {variant === 'initial' && (
            <SecondaryButton onClick={handleManualReview}>
              {t('actions.review_manually')}
            </SecondaryButton>
          )}
          <PrimaryButton
            onClick={handleApply}
            disabled={checkedNames.size === 0}
          >
            {t('actions.apply_updates')}
          </PrimaryButton>
        </Stack>
      </DataEntryModal.Footer>
    </DataEntryModal>
  );
};

export default CalibrationNudgeModal;
